import { FaCalendar, FaClock } from "react-icons/fa";
import { useInView } from "../hooks/useInView";
import { useState } from "react";

const BlogSection = () => {
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const blogs = [
    {
      title: "How to Get Instant Salary Loan Online in 2024",
      excerpt: "Learn the step-by-step process to apply for an instant salary loan online. Quick approval, minimal documentation, and fast disbursal guaranteed.",
      date: "Dec 15, 2024",
      readTime: "5 min read",
      category: "Loan Guide",
      image: <span className="text-white font-bold">₹</span>,
    },
    {
      title: "Documents Required for Personal Loan Application",
      excerpt: "Complete checklist of documents needed for personal loan approval. Make your application process smooth with proper documentation.",
      date: "Dec 10, 2024",
      readTime: "4 min read",
      category: "Documentation",
      image: "📄",
    },
    {
      title: "Understanding Loan Processing Fees and Charges",
      excerpt: "Transparent breakdown of all fees and charges associated with personal loans. Know exactly what you're paying for.",
      date: "Dec 5, 2024",
      readTime: "6 min read",
      category: "Finance Tips",
      image: "💳",
    },
    {
      title: "Benefits of Digital Loan Applications",
      excerpt: "Discover why digital loan applications are faster, safer, and more convenient than traditional methods. Go paperless today!",
      date: "Nov 28, 2024",
      readTime: "5 min read",
      category: "Technology",
      image: "📱",
    },
  ];

  return (
    <section ref={ref} className="w-full py-16 md:py-20 lg:py-24 bg-gradient-to-br from-[#A9F9EE] via-[#7DD4D0] to-[#21A6B5] relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#3D4A7C] rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-float" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-block mb-4">
            <span className="px-4 py-1 bg-gradient-to-r from-[#5DADE2] to-[#3D4A7C] text-white text-base leading-6 font-semibold text-center font-['Fredoka'] rounded-full shadow-md">
              📚 Expert Insights
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-[60px] lg:leading-[60px] font-bold text-center text-[#323232] mb-4 font-['Inter'] drop-shadow-md">
            Latest Insights & Tips
          </h2>
          <p className="text-lg md:text-xl lg:text-[24px] lg:leading-[32px] font-normal text-center text-[#1A1A1A] font-['Inter']">
            Stay informed with our expert guides on loans and financial planning
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {blogs.map((blog, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`group bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-700 cursor-pointer relative ${
                isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              } ${
                hoveredIndex === index ? 'scale-105 -translate-y-3' : 'scale-100'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Shine Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>

              {/* Image/Icon Section */}
              <div className={`bg-gradient-to-br from-[#3D4A7C] to-[#5DADE2] h-40 md:h-44 lg:h-48 flex items-center justify-center text-6xl md:text-7xl relative overflow-hidden transition-all duration-500 ${
                hoveredIndex === index ? 'scale-110' : 'scale-100'
              }`}>
                {/* Floating particles in image */}
                <div className="absolute inset-0 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute top-10 left-10 w-3 h-3 bg-white rounded-full animate-float"></div>
                  <div className="absolute top-20 right-10 w-2 h-2 bg-white rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
                  <div className="absolute bottom-10 left-1/3 w-2.5 h-2.5 bg-white rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
                </div>
                <div className={`transition-all duration-500 ${
                  hoveredIndex === index ? 'scale-125 rotate-12' : 'scale-100 rotate-0'
                }`}>
                  {blog.image}
                </div>
              </div>

              {/* Content Section */}
              <div className="p-5 md:p-6 relative">
                <div className="flex items-center gap-2 mb-4">
                  <span className={`px-3 py-1 bg-[#4A6B9D] text-white text-xs font-semibold font-['Fredoka'] rounded-full transition-all duration-300 ${
                    hoveredIndex === index ? 'scale-110' : 'scale-100'
                  }`}>
                    {blog.category}
                  </span>
                </div>

                <h3 className={`text-lg md:text-[20px] md:leading-[28px] font-bold text-[#2C3E50] mb-4 font-['Inter'] transition-all duration-300 ${
                  hoveredIndex === index ? 'text-[#5DADE2] scale-105' : 'text-[#2C3E50] scale-100'
                }`}>
                  {blog.title}
                </h3>

                <p className="text-gray-600 text-sm md:text-base md:leading-[26px] font-normal mb-6 font-['Inter'] transition-colors duration-300 group-hover:text-gray-800">
                  {blog.excerpt}
                </p>

                <div className="flex items-center justify-between text-xs md:text-sm text-gray-500 font-['Fredoka']">
                  <div className={`flex items-center gap-1 transition-all duration-300 ${
                    hoveredIndex === index ? 'scale-110 text-[#3D4A7C]' : 'scale-100'
                  }`}>
                    <FaCalendar className="text-[#3D4A7C]" />
                    <span>{blog.date}</span>
                  </div>
                  <div className={`flex items-center gap-1 transition-all duration-300 ${
                    hoveredIndex === index ? 'scale-110 text-[#3D4A7C]' : 'scale-100'
                  }`}>
                    <FaClock className="text-[#3D4A7C]" />
                    <span>{blog.readTime}</span>
                  </div>
                </div>

                {/* Animated Border */}
                <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#5DADE2] to-[#3D4A7C] transition-all duration-500 ${
                  hoveredIndex === index ? 'w-full' : 'w-0'
                }`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
